// todo handle storage rules

import {
	deleteObject,
	getBytes,
	getDownloadURL,
	getStorage,
	ref,
	uploadBytes,
	uploadBytesResumable,
} from "firebase/storage";
import { app } from "./firebaseApp";

export const storage = getStorage(app);

export const storageApi = {
	uploadFileResumable: (payload: { folder: string; name: string; file: File }) => {
		const { name, folder, file } = payload;
		const fileRef = ref(storage, `${folder}/${name}`);
		const uploadTask = uploadBytesResumable(fileRef, file);
		console.log("uploadTask", uploadTask);

		return uploadTask;
	},
	uploadFile: async (payload: { folder: string; name: string; file: File; type: string }) => {
		const { name, folder, file, type } = payload;
		try {
			const fileRef = ref(storage, `${folder}/${name}`);
			const response = await uploadBytes(fileRef, file);
			const fullUrl = await getDownloadURL(response.ref);
			const extension = file.name?.split(".").at(-1);

			return {
				success: true,
				type,
				name,
				folder,
				size: response.metadata.size,
				contentType: response.metadata.contentType as any,
				fullUrl: fullUrl,
				extension,
			};
		} catch (error: any) {
			return {
				success: false,
				type,
				errorMessage: error.message,
				size: 0,
				name: payload.name,
			};
		}
	},
	/** Copy a file to a new path (download, then upload — the client SDK has no
	 *  server-side copy) and return the copy's download URL. Throws on failure,
	 *  so a caller can tell a copy that did not happen from one that did — and
	 *  a copy that was written but whose URL could not be read is deleted
	 *  before throwing, so a failed copy never leaves a file behind. */
	async copyFile(fromPath: string, toPath: string, contentType?: string): Promise<string> {
		const bytes = await getBytes(ref(storage, fromPath));
		const copied = await uploadBytes(ref(storage, toPath), bytes, contentType ? { contentType } : undefined);
		try {
			return await getDownloadURL(copied.ref);
		} catch (error) {
			await deleteObject(copied.ref).catch(() => undefined);
			throw error;
		}
	},
	async remove(path: string) {
		try {
			const fileRef = ref(storage, path);
			await deleteObject(fileRef);

			console.log("File deleted successfully");
		} catch (error) {
			console.error("Error deleting file:", error);
		}
	},

	/**
	 * Upload a base64 data URI (data:image/png;base64,...) to Firebase Storage at
	 * a deterministic per-doctor path and return the download URL.
	 *
	 * Throws on:
	 *   - unsupported / malformed data URI (anything other than image/png|jpeg|jpg)
	 *   - decoded bytes > 5 MB (per-image hard ceiling, matches storage rule)
	 *   - any underlying SDK error (network, rules rejection)
	 */
	async uploadBase64Image(payload: {
		doctorUid: string;
		dataUri: string;
		widthPx?: number;
		heightPx?: number;
	}): Promise<{ url: string; path: string }> {
		const { doctorUid, dataUri, widthPx, heightPx } = payload;

		const match = dataUri.match(/^data:image\/(png|jpe?g);base64,(.+)$/i);
		if (!match) throw new Error("unsupportedImageType");

		const ext = match[1].toLowerCase() === "png" ? "png" : "jpg";
		const bytes = Uint8Array.from(atob(match[2]), (c) => c.charCodeAt(0));

		if (bytes.byteLength > 5 * 1024 * 1024) throw new Error("imageTooLarge");

		const digest = await crypto.subtle.digest("SHA-256", bytes);
		const hash = Array.from(new Uint8Array(digest))
			.map((b) => b.toString(16).padStart(2, "0"))
			.join("");

		// Encode DOCX display dimensions in filename so the PDF renderer can
		// scale images to original size without any TipTap attr changes.
		const dimSuffix = (widthPx && widthPx > 0 && heightPx && heightPx > 0)
			? `_${Math.round(widthPx)}x${Math.round(heightPx)}`
			: "";
		const path = `template_images/${doctorUid}/${hash}${dimSuffix}.${ext}`;
		const fileRef = ref(storage, path);

		await uploadBytes(fileRef, bytes, {
			contentType: `image/${ext === "jpg" ? "jpeg" : "png"}`,
		});

		const url = await getDownloadURL(fileRef);
		return { url, path };
	},
};
